import { useNavigate } from 'react-router-dom';

import { VerticalContainer, useForm } from '@american-autonomy/ag-ui';

import Form from 'components/Form';
import FormSubmitPanel from 'components/FormSubmitPanel';
import SelectApplicationField from 'components/fields/SelectApplicationField';
import BoundaryFormFields from 'components/forms/BoundaryFormFields';

import useApplication from 'hooks/useApplication';
import useBoundary from 'hooks/useBoundary';
import useField from 'hooks/useField';
import useFormDiscardConfirmation from 'hooks/useFormDiscardConfirmation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import { APPLICATION_FIELD_NAME, TEST_ID } from './constants';
import type { CreateBoundaryFormSchema, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const CreateBoundaryForm: FunctionComponent<Props> = ({
    className = '',
    onCancelClose,
    onClose,
    requestingClose,
    testId = TEST_ID
}) => {
    const { CREATE, NEXT_DRAW_THE_PERIMETER } = useTranslation();
    const { createBoundary } = useBoundary();
    const { assignBoundaryToApplication } = useApplication();
    const { assignFieldToBoundary } = useField();
    const navigate = useNavigate();
    const { boundaryDetailsPage } = usePageRoutes();

    const form = useForm<CreateBoundaryFormSchema>({
        mode: 'onChange'
    });

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit
    } = form;

    const { confirmationPanel, showConfirmation } = useFormDiscardConfirmation({
        isDirty,
        onCancelClose,
        onClose,
        requestingClose
    });

    const onSubmit = async (data: CreateBoundaryFormSchema) => {
        const { application, field, name } = data;

        const boundary = await createBoundary({ boundary: { boundaryParts: [], name } });

        if (boundary) {
            if (application) {
                await assignBoundaryToApplication({
                    applicationId: application.value,
                    boundaryId: boundary.id
                });
            }

            if (field) {
                await assignFieldToBoundary({
                    boundaryId: boundary.id,
                    siteId: field.value
                });
            }
            onClose?.();
            navigate(boundaryDetailsPage(boundary.id), { state: { isEditingMap: true } });
        }
    };

    return (
        <VerticalContainer className={`${styles.createBoundaryForm} ${className}`} testId={testId}>
            {showConfirmation ? (
                confirmationPanel
            ) : (
                <Form form={form}>
                    <BoundaryFormFields>
                        <SelectApplicationField<CreateBoundaryFormSchema>
                            control={control}
                            name={APPLICATION_FIELD_NAME}
                        />
                    </BoundaryFormFields>

                    <FormSubmitPanel
                        buttonText={CREATE}
                        errors={errors}
                        nextText={NEXT_DRAW_THE_PERIMETER}
                        onSubmit={handleSubmit(onSubmit)}
                    />
                </Form>
            )}
        </VerticalContainer>
    );
};

export default CreateBoundaryForm;
